import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from '../../../i18n/useTranslation';
import { getContactTypes } from '../../../components/shopping-cart/services/shopping-cart.service';
import { getLanguages, getLanguageCode } from '../../../shared/services/language.service';
import { getCountries } from '../../../components/order/services';
import { getCountryName } from '../../../shared/services/country.service';
import { Language } from '../../../shared/models';
import { Country } from '../../../components/order/models';
import { IContactTypeListView } from '../../../components/order/interfaces/contact-type-list-view.interface';
import { createAddContractorContactValidationSchema } from './add-contractor-contact-dialog.validation';

export interface NewContractorPersonFormValues {
    id: string;
    contactTypeId: string;
    title: string;
    firstName: string;
    lastName: string;
    phone: string;
    mobile: string;
    email: string;
    languageId: string;
    country: string;
    comments: string;
    communicationAccepted: boolean;
}

interface Prop {
    isOpen: boolean;
    handleCloseDialog: Function;
}

const rowSx = {
    display: 'grid',
    alignItems: 'center',
    columnGap: 3,
    mb: 2,
    mt: 2,
};

const AddContractorContactDialog: React.FC<Prop> = ({ isOpen, handleCloseDialog }) => {
    const { t } = useTranslation('translation');
    const [contactTypes, setContactTypes] = useState<IContactTypeListView[]>([]);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [countries, setCountries] = useState<Country[]>([]);

    const validationSchema = useMemo(() => createAddContractorContactValidationSchema(t), [t]);

    const formik = useFormik<NewContractorPersonFormValues>({
        initialValues: {
            id: '',
            contactTypeId: '',
            title: '',
            firstName: '',
            lastName: '',
            phone: '',
            mobile: '',
            email: '',
            languageId: '',
            country: '',
            comments: '',
            communicationAccepted: false,
        },
        validationSchema,
        onSubmit: (values) => {
            handleCloseDialog({ ...values, id: crypto.randomUUID() });
        },
    });

    const sortedCountries = useMemo(() => {
        return [...(countries ?? [])].sort((a, b) =>
            getCountryName(a.code, getLanguageCode()).localeCompare(getCountryName(b.code, getLanguageCode()))
        );
    }, [countries]);

    useEffect(() => {
        if (isOpen) {
            formik.resetForm();
            getContactTypes().then((response) => {
                if (response) {
                    setContactTypes(response.data as IContactTypeListView[]);
                }
            });
            getLanguages().then((response) => {
                if (response) {
                    setLanguages(response.data as Language[]);
                }
            });
            getCountries().then((response) => {
                if (response) {
                    setCountries(response.data as Country[]);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleClose = () => {
        handleCloseDialog(null);
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
            <Box component="form" onSubmit={formik.handleSubmit}>
                <DialogTitle
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    {t('ADD_CONTACT')}

                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ position: 'relative' }}>
                    <Box sx={rowSx}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            select
                            fullWidth
                            id="contactTypeId"
                            name="contactTypeId"
                            label={t('CONTACT_TYPE')}
                            variant="outlined"
                            size="small"
                            value={formik.values.contactTypeId}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.contactTypeId && Boolean(formik.errors.contactTypeId)}
                            helperText={formik.touched.contactTypeId && formik.errors.contactTypeId}
                        >
                            {contactTypes?.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {t(`contactTypes.${item.code}`, { defaultValue: item.name })}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Box sx={rowSx}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            select
                            fullWidth
                            id="title"
                            name="title"
                            label={t('TITLE')}
                            variant="outlined"
                            size="small"
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.title && Boolean(formik.errors.title)}
                            helperText={formik.touched.title && formik.errors.title}
                        >
                            <MenuItem value="mr">{t('TITLE_MR')}</MenuItem>
                            <MenuItem value="mrs">{t('TITLE_MRS')}</MenuItem>
                            <MenuItem value="miss">{t('TITLE_MISS')}</MenuItem>
                            <MenuItem value="ms">{t('TITLE_MS')}</MenuItem>
                            <MenuItem value="other">{t('TITLE_OTHER')}</MenuItem>
                        </TextField>
                    </Box>

                    <Box sx={rowSx}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            id="firstName"
                            name="firstName"
                            label={t('FIRST_NAME')}
                            variant="outlined"
                            size="small"
                            value={formik.values.firstName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                            helperText={formik.touched.firstName && formik.errors.firstName}
                            autoComplete="given-name"
                        />
                    </Box>

                    <Box sx={rowSx}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            id="lastName"
                            name="lastName"
                            label={t('LAST_NAME')}
                            variant="outlined"
                            size="small"
                            value={formik.values.lastName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                            helperText={formik.touched.lastName && formik.errors.lastName}
                            autoComplete="family-name"
                        />
                    </Box>

                    <Box sx={rowSx}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            id="phone"
                            name="phone"
                            label={t('PHONE')}
                            variant="outlined"
                            size="small"
                            value={formik.values.phone}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.phone && Boolean(formik.errors.phone)}
                            helperText={formik.touched.phone && formik.errors.phone}
                            autoComplete="tel"
                        />
                    </Box>

                    <Box sx={rowSx}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            id="mobile"
                            name="mobile"
                            label={t('MOBILE')}
                            variant="outlined"
                            size="small"
                            value={formik.values.mobile}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                            helperText={formik.touched.mobile && formik.errors.mobile}
                            autoComplete="tel"
                        />
                    </Box>

                    <Box sx={rowSx}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            id="email"
                            name="email"
                            label={t('EMAIL')}
                            variant="outlined"
                            size="small"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.email && Boolean(formik.errors.email)}
                            helperText={formik.touched.email && formik.errors.email}
                            autoComplete="email"
                        />
                    </Box>

                    <Box sx={rowSx}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            select
                            fullWidth
                            id="languageId"
                            name="languageId"
                            label={t('LANGUAGE')}
                            variant="outlined"
                            size="small"
                            value={formik.values.languageId}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.languageId && Boolean(formik.errors.languageId)}
                            helperText={formik.touched.languageId && formik.errors.languageId}
                        >
                            {languages?.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {t(`languages.${item.code}`)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Box sx={rowSx}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            select
                            fullWidth
                            id="country"
                            name="country"
                            label={t('COUNTRY')}
                            variant="outlined"
                            size="small"
                            value={formik.values.country}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.country && Boolean(formik.errors.country)}
                            helperText={formik.touched.country && formik.errors.country}
                        >
                            {sortedCountries?.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {getCountryName(item.code, getLanguageCode())}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Box sx={rowSx}>
                        <TextField
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            multiline
                            rows={4}
                            id="comments"
                            name="comments"
                            label={t('COMMENTS')}
                            variant="outlined"
                            size="small"
                            value={formik.values.comments}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.comments && Boolean(formik.errors.comments)}
                            helperText={formik.touched.comments && formik.errors.comments}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    id="communicationAccepted"
                                    name="communicationAccepted"
                                    checked={formik.values.communicationAccepted}
                                    onChange={formik.handleChange}
                                />
                            }
                            label={t('COMMUNICATION_ACCEPTED')}
                        />
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        bgcolor: 'background.paper',
                        borderTop: 1,
                        borderColor: 'divider',
                        px: 3,
                        py: 2,
                    }}
                >
                    <Button
                        onClick={handleClose}
                        sx={{
                            textTransform: 'capitalize',
                        }}
                    >
                        {t('CANCEL')}
                    </Button>

                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            bgcolor: '#821818',
                            '&:hover': {
                                bgcolor: '#6d1414',
                            },
                            textTransform: 'capitalize',
                        }}
                    >
                        {t('SAVE')}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
export default AddContractorContactDialog;
